import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { fallback, zodValidator } from "@tanstack/zod-adapter";
import { Suspense } from "react";
import { z } from "zod";
import { serversQueryOptions } from "@/app/servers/api/get-servers";
import { ServersListSkeleton } from "@/app/servers/components/servers-list-skeleton";
import { ServersList } from "@/app/servers/list/list";
import { nameFilterSchema } from "@/components/custom/filters/name-filter";

const serverTypeSchema = z.enum(["list", "new"]);

const serversParamsSchema = z.object({
  type: fallback(serverTypeSchema, "list").default("list"),
  defaultValues: fallback(
    z
      .object({
        id: z.string().optional(),
        uri: z.string().optional(),
        secret: z.string().optional(),
        timeout: z.number().optional(),
        retry: z.number().optional(),
      })
      .optional(),
    {
      id: "",
      uri: "",
      secret: "",
      timeout: 30,
      retry: 3,
    }
  ).default({
    id: "",
    uri: "",
    secret: "",
    timeout: 30,
    retry: 3,
  }),
  serverTypes: fallback(z.array(z.string()), []).default([]),
  statuses: fallback(z.array(z.string()), []).default([]),
  ...nameFilterSchema.shape,
});

const defaultValues = {
  type: "list" as const,
  name: "",
  serverTypes: [],
  statuses: [],
  defaultValues: {
    id: "",
    uri: "",
    secret: "",
    timeout: 30,
    retry: 3,
  },
};

export const Route = createFileRoute(
  "/_authenticated/orgs/$organizationId/projects/$projectId/servers/"
)({
  validateSearch: zodValidator(serversParamsSchema),
  search: {
    middlewares: [stripSearchParams(defaultValues)],
  },
  component: (): React.JSX.Element => (
    <Suspense fallback={<ServersListSkeleton />}>
      <ServersList />
    </Suspense>
  ),
  beforeLoad: ({ context: { queryClient }, params }) => {
    queryClient.prefetchQuery(
      serversQueryOptions({ binding: "project", scope: params })
    );
  },
  loader: () => ({
    crumb: "Servers",
  }),
});
